import { create } from 'zustand'
import { devtools, subscribeWithSelector, persist } from 'zustand/middleware'
import { toast } from 'react-toastify'
import { Matrix4 } from 'three'
import { applyMaterialModes, styleCompareOverlayMaterial } from '../constants/materialConfig'
import { syncCompareBaseline } from '../lib/compareBaselineSnapshot'
import { clearPreModificationSocket } from '../lib/preModificationSnapshot'
import {
  applyAlignmentSocketAnglesToMesh,
  getAlignmentSocketQBaseArray,
  rederiveAlignmentSocketQBaseFromMesh,
  setAlignmentSocketQBaseFromArray,
} from '../lib/socketAlignRedRing'
import {
  capturePreparingScanSocketRotationFromMesh,
  copyMeshTransform,
  restorePreparingScanSocketRotation,
} from '../lib/preparingScanSocketTransform'
import { logLockingSocketScale, logScaleIfChanged, trackMeshScale } from '../lib/lockingSocketScaleDebug'
import { captureUndoSnapshot, peekUndoSnapshot } from '../../features/step-checkpoints/undoSnapshot'
import { stripPreviewTint } from '../lib/previewTint'
import { releaseAllOrbitParks } from '../lib/orbitControlsGuard'
// Leaf seam only — the real recorder needs the workflow store, which imports this
// store, so it registers itself instead of being imported here.
import { notifyToolApplied } from '../../features/step-checkpoints/undo/applyRecorder'
import useMLReductionStore from './useMLReductionStore'

// ---------------------------------------------------------------------------
// Loading overlay ownership
//
// `isLoading` is one global boolean with ~20 independent writers — every tool
// apply, both ThreeScene load effects, the draft store, the checkpoint loaders
// and the step revert. Nothing tracks *who* turned it on, so the last writer
// wins in both directions: an inner operation's `setIsLoading(false)` tears down
// an outer operation's overlay while it is still running, and an owner that
// loses its clear path (an effect cleanup that never re-fires, a promise that
// never settles) leaves the overlay up with no one able to say why.
//
// `beginLoading`/`endLoading` add scoped, ref-counted owners on top, without
// touching the existing call sites: a legacy `setIsLoading(false)` can no longer
// tear down an overlay a scoped owner is still holding. `LOADING_TRACE` logs
// every transition — including the suppressed ones — so a stuck overlay names
// its owner instead of having to be guessed at.
// ---------------------------------------------------------------------------

const LOADING_LOG = '[Loading]'
const LOADING_TRACE = !!import.meta.env?.DEV
let loadingTokenSeq = 0

const loadingNow = () =>
  typeof performance !== 'undefined' ? performance.now() : Date.now()

/** Best-effort "who called this", for the trace. */
function loadingCaller() {
  if (!LOADING_TRACE) return ''
  const frames = new Error().stack?.split('\n') ?? []
  // 0 Error, 1 loadingCaller, 2 traceLoading, 3 the store action, 4 the caller.
  const frame = frames[4] ?? frames[3] ?? ''
  return frame.trim().replace(/^at\s+/, '').replace(/\?[^\s)]*/, '')
}

function traceLoading(event, { was, now, message, owners }) {
  if (!LOADING_TRACE) return
  const held = owners.map((o) => o.owner).join(', ') || '—'
  console.log(
    `${LOADING_LOG} ${event} ${was ? 'on' : 'off'}→${now ? 'on' : 'off'}` +
      ` msg=${JSON.stringify(message ?? null)} held=[${held}] from=${loadingCaller()}`,
  )
}

const useEditorStore = create(
  persist(
    devtools(
      subscribeWithSelector((set, get) => ({
      // ===== SCENE MANAGEMENT =====
      sceneRefs: {
        scene: null,
        camera: null,
        renderer: null,
        controls: null,
        shadedMesh: null,
        material: null,
        unmodifiedMesh: null,
        originalCamera:null,
        normalMaterial: null,
      },  /* scene, camera, renderer, controls, shadedMesh, unmodifiedMesh*/
      setSceneRefs: (refs) => set({ sceneRefs: refs }),

      // Geometry version tracking for cache invalidation across tools
      // Incremented when geometry is modified in-place (e.g., applyMatrix4, vertex operations)
      geometryVersion: 0,
      incrementGeometryVersion: () => {
        const newVersion = get().geometryVersion + 1;
        console.log('🔄 Geometry version incremented:', newVersion);
        set({ geometryVersion: newVersion });
      },

      /** Engulf form rim plane (world space) saved when locking attachment is applied. */
      logoPlacementBoundary: null,
      setLogoPlacementBoundary: (logoPlacementBoundary) => set({ logoPlacementBoundary }),

      /** Bumped when socket move/rotate gizmo changes the mesh (preparing scan). */
      socketTransformRevision: 0,
      bumpSocketTransformRevision: () =>
        set((s) => ({ socketTransformRevision: s.socketTransformRevision + 1 })),

      /** Preparing-scan socket Euler XYZ (degrees); survives tool switches / gizmo detach. */
      preparingScanSocketEulerDeg: { x: 0, y: 0, z: 0 },
      setPreparingScanSocketEulerDeg: (preparingScanSocketEulerDeg) =>
        set({ preparingScanSocketEulerDeg }),

      /**
       * Swap the editable shaded mesh (disposes previous geometry).
       * @param {import('three').Mesh} newMesh
       * @param {{ reframeCompareBaseline?: boolean }} [opts]
       *   reframeCompareBaseline — the operation bakes a different local frame into the vertices
       *   (e.g. locking CSG output is world-baked). The compare baseline geometry is re-expressed in
       *   the new mesh's frame so it stays put in world space. The baseline is *never* replaced with
       *   modified geometry: compare mode always shows the original loaded model.
       */
      replaceShadedMesh: (newMesh, opts = {}) => {
        const { reframeCompareBaseline = false, keepNewMeshMaterial = false, keepNewMeshTransform = false } = opts;
        const { sceneRefs, setSceneRefs, incrementGeometryVersion } = useEditorStore.getState();

        if (!sceneRefs || !sceneRefs.scene || !sceneRefs.shadedMesh) {
            console.error("Cannot replace mesh, scene references are missing.");
            return;
        }

        const scene = sceneRefs.scene;
        const oldMesh = sceneRefs.shadedMesh;

        // Frame the compare baseline geometry currently lives in (its TRS tracks the shaded mesh).
        oldMesh.updateMatrixWorld(true);
        const priorCompareFrame = reframeCompareBaseline ? oldMesh.matrixWorld.clone() : null;

        // console.log('🔄 EditorStore: Replacing shaded mesh:', oldMesh.name, 'with:', newMesh.name);

        // 1. Remove the old mesh from the scene graph
        scene.remove(oldMesh);
        // It's also good practice to dispose of old geometry to free GPU memory
        oldMesh.geometry.dispose();

        // 2. Add the new mesh to the scene graph
        scene.add(newMesh);

        // 3. Keep the viewer material (CSG/attachment meshes often ship Phong without vertex colors)
        const priorMaterial = oldMesh.material;
        if (!keepNewMeshMaterial && priorMaterial) {
          const nextMaterial = priorMaterial.clone?.() ?? priorMaterial;
          if (newMesh.material && newMesh.material !== nextMaterial) {
            newMesh.material.dispose?.();
          }
          newMesh.material = nextMaterial;
        } else if (keepNewMeshMaterial && priorMaterial && newMesh.material && priorMaterial !== newMesh.material) {
          priorMaterial.dispose?.();
        }

        const { ghostModeEnabled, colorModeEnabled } = get();
        applyMaterialModes(newMesh.material, ghostModeEnabled, colorModeEnabled, newMesh.geometry);

        // Preserve socket TRS (lasso/fill/etc. often create meshes at identity rotation).
        if (!keepNewMeshTransform) {
          copyMeshTransform(oldMesh, newMesh);
        }

        // 4. Update the state to point to the new mesh
        setSceneRefs({ ...sceneRefs, shadedMesh: newMesh, material: newMesh.material });
        if (newMesh.userData?.logoPlacementBoundary) {
          get().setLogoPlacementBoundary(newMesh.userData.logoPlacementBoundary);
        }
        capturePreparingScanSocketRotationFromMesh(newMesh);
        if (!newMesh.userData?.lockingWorldBaked) {
          restorePreparingScanSocketRotation(newMesh);
          rederiveAlignmentSocketQBaseFromMesh();
        }

        // 5. Compare overlay: keep the original-model baseline, only re-frame it when the operation
        //    baked a new local frame into the vertices (compare TRS is copied from shadedMesh each frame).
        const um = useEditorStore.getState().sceneRefs?.unmodifiedMesh;
        if (priorCompareFrame && um?.geometry) {
          newMesh.updateMatrixWorld(true);
          const reframe = new Matrix4()
            .copy(newMesh.matrixWorld).invert().multiply(priorCompareFrame);
          um.geometry.applyMatrix4(reframe);
          if (um.geometry.boundsTree) {
            um.geometry.disposeBoundsTree();
            um.geometry.boundsTree = null;
          }
          um.geometry.computeVertexNormals();
          um.geometry.computeBoundingBox();
          um.geometry.computeBoundingSphere();
          um.position.copy(newMesh.position);
          um.quaternion.copy(newMesh.quaternion);
          um.scale.copy(newMesh.scale);
          um.updateMatrixWorld(true);
          styleCompareOverlayMaterial(um.material, useEditorStore.getState().showUnmodifiedMesh);
          // Keep the load-time snapshot in the same frame, so a later checkpoint
          // reload restores the baseline registered against the socket.
          syncCompareBaseline(um);
        }
        if (priorCompareFrame) {
          // The compare baseline is re-framed above so it stays registered with
          // the socket; the pre-modification snapshot cannot be (its geometry is
          // shared with the undo command, which needs the old frame). Drop it
          // rather than measure "Scan" off geometry in a stale frame.
          clearPreModificationSocket();
        }

        // 6. Increment geometry version to invalidate caches in tools (e.g., FlaresHandler)
        incrementGeometryVersion();
      },

      restoreUnmodifiedAsShaded: () => {
        const { sceneRefs, setSceneRefs, incrementGeometryVersion } = useEditorStore.getState();
        const scene = sceneRefs?.scene;
        const shadedMesh = sceneRefs?.shadedMesh;
        const unmodifiedMesh = sceneRefs?.unmodifiedMesh;

        if (!scene || !unmodifiedMesh) {
          console.warn('Cannot restore unmodified mesh: missing scene or unmodified mesh.');
          return;
        }

        // Build a fresh editable mesh from the unmodified reference.
        const restoredMesh = unmodifiedMesh.clone();
        restoredMesh.geometry = unmodifiedMesh.geometry.clone();
        restoredMesh.material = shadedMesh?.material?.clone?.() || unmodifiedMesh.material?.clone?.() || unmodifiedMesh.material;
        restoredMesh.visible = true;
        restoredMesh.renderOrder = 0;

        if (shadedMesh) {
          scene.remove(shadedMesh);
          shadedMesh.geometry?.dispose?.();
          shadedMesh.material?.dispose?.();
        }

        scene.add(restoredMesh);
        unmodifiedMesh.visible = false;
        styleCompareOverlayMaterial(unmodifiedMesh.material, false);

        set({
          showUnmodifiedMesh: false,
        });

        setSceneRefs({
          ...sceneRefs,
          shadedMesh: restoredMesh,
          material: restoredMesh.material,
          unmodifiedMesh,
        });

        incrementGeometryVersion();
      },

      replaceCameraAndControls: (newCamera, newControls) => {
        const {sceneRefs, setSceneRefs} = useEditorStore.getState();
        if (!sceneRefs || !sceneRefs.camera || !sceneRefs.controls) {
          console.error("Cannot replace camera and controls, scene references are missing.");
          return;
        }
        setSceneRefs({ ...sceneRefs, camera: newCamera, controls: newControls})
      },

      showUnmodifiedMesh: false,
      toggleUnmodifiedMesh: () => {
        const um = get().sceneRefs?.unmodifiedMesh;
        // No fallback: without a saved original there is nothing to compare against, and silently
        // showing the current (modified) mesh would look like a working comparison.
        if (!um?.geometry?.attributes?.position?.count) {
          if (get().showUnmodifiedMesh) set({ showUnmodifiedMesh: false });
          toast.error('No original model saved for this step — comparison is unavailable.');
          return;
        }
        const next = !get().showUnmodifiedMesh;
        um.visible = next;
        styleCompareOverlayMaterial(um.material, next);
        set({ showUnmodifiedMesh: next });
      },

      ghostModeEnabled: false,
      toggleGhostMode: () => {
        const { sceneRefs } = get();
        const material = sceneRefs?.material;
        const geometry = sceneRefs?.shadedMesh?.geometry;

        if (!material) return;

        const newGhostState = !get().ghostModeEnabled;
        const colorModeState = get().colorModeEnabled;

        applyMaterialModes(material, newGhostState, colorModeState, geometry);
        set({ ghostModeEnabled: newGhostState });
      },

      colorModeEnabled: false,
      toggleColorMode: () => {
        const { sceneRefs } = get();
        const material = sceneRefs?.material;
        const geometry = sceneRefs?.shadedMesh?.geometry;

        if (!material) return;

        const ghostModeState = get().ghostModeEnabled;
        const newColorState = !get().colorModeEnabled;

        applyMaterialModes(material, ghostModeState, newColorState, geometry);
        set({ colorModeEnabled: newColorState });
      },

      // Scan / preparing step — scene helpers (not persisted)
      scanPreparingSixAxisVisible: false,
      toggleScanPreparingSixAxis: () =>
        set({ scanPreparingSixAxisVisible: !get().scanPreparingSixAxisVisible }),

      scaleOverlayVisible: false,
      toggleScaleOverlay: () =>
        set({ scaleOverlayVisible: !get().scaleOverlayVisible }),

      /** Distances from scale L-corner along world X/Y (mm); null when no hover hit */
      scaleHoverReadout: null,
      setScaleHoverReadout: (readout) => set({ scaleHoverReadout: readout }),

      normalModeEnabled: false,
      toggleNormalMode: () => {
        const { sceneRefs } = get();
        const shadedMesh = sceneRefs?.shadedMesh;
        const normalMaterial = sceneRefs?.normalMaterial;
        const originalMaterial = sceneRefs?.material;

        if (!shadedMesh || !normalMaterial || !originalMaterial) return;

        const newNormalState = !get().normalModeEnabled;
        shadedMesh.material = newNormalState ? normalMaterial : originalMaterial;
        set({ normalModeEnabled: newNormalState });
      },

      interactiveObjects: [],
      setInteractiveObjects: (objects) => set({ interactiveObjects: objects }),

      cursorOverride: null, // Can be 'none', 'crosshair', etc.
      setCursorOverride: (style) => set({ cursorOverride: style }),

      // Painting cursor UI (circle overlay)
      paintingCursor: { enabled: false, radiusPx: 10, color: '#ffffff' },
      setPaintingCursor: (data) => set(state => ({ paintingCursor: { ...state.paintingCursor, ...data } })),

      // ===== TOOL MANAGEMENT =====
      activeTool: 'none',
      setActiveTool: (tool) => set({ activeTool: tool }),

      // Measurement-category tool ids for the current step. These are pulled out
      // of the left step toolbar and rendered on the scene overlay instead (right
      // rail, under the info-panel gear) by `MeasurementToolButtons`.
      measurementToolIds: [],
      setMeasurementToolIds: (toolIds) =>
        set((state) => {
          const next = toolIds || [];
          const prev = state.measurementToolIds;
          if (prev.length === next.length && prev.every((id, i) => id === next[i])) return state;
          return { measurementToolIds: next };
        }),

      // Tool settings for each tool type
      toolSettings: {
        // Scan Process - Preparing Scan
        scan_preparing_scissors: {
          enabled: true
        },
        scan_preparing_eraser: {
          enabled: true
        },
        scan_preparing_pencil: {
          enabled: true
        },
        
        // Scan Process - Axes Alignment
        scan_axes_pin: {
          enabled: true
        },
        scan_axes_movement: {
          showLasers: true,
          currentView: 'face'
        },
        
        // Main Process - Rectification
        main_rectification_measure_reduction: {
          enabled: true
        },
        main_rectification_scoop_recharge: {
          enabled: true
        },
        main_rectification_blocking_zone: {
          enabled: true
        },
        main_rectification_global_reduction: {
          enabled: true
        },
        
        // Main Process - Elongation
        main_elongation_elongation: {
          enabled: false,
          value: 0
        },
        
        // Main Process - Trimline
        main_trimline_cut: {
          thickness: 0.1,
          smooth: true
        },
        main_trimline_line: {
          enabled: true
        },
        main_trimline_measure: {
          unit: 'mm',
          precision: 2
        },
        
        // Main Process - Flares
        main_flares_flare: {
          enabled: true
        },
        main_flares_smoothning: {
          enabled: true
        },
        main_flares_adjust: {
          enabled: true
        },
        main_flares_preview: {
          enabled: true
        },

        // Main Process - ML Reduction
        main_ml_reduction: {
          size: 1,
        },
        main_ml_reduction_smoothning: {
          enabled: true,
        },
        
        // Main Process - Thickness
        main_thickness_thickness: {
          value: 3.0,
          unit: 'mm'
        },
        main_thickness_measure: {
          unit: 'mm',
          precision: 2
        },
        main_thickness_adjust: {
          enabled: true
        },
        
        // Main Process - Locking Mechanism (removed - now managed by dedicated store)
        main_locking_position: {
          enabled: true
        },
        main_locking_adjust: {
          enabled: true
        },
        
        // Main Process - Alignment
        
        // Main Process - Valve
        main_valve_valve: {
          enabled: true
        },
        main_valve_position: {
          enabled: true
        },
        main_valve_adjust: {
          enabled: true
        },

        // Main Process - Logo placement
        main_logo_placement: {
          textureScale: 0.08,
          offsetX: 0,
          offsetY: 0,
          patchHalfSizeCm: 2.5,
          rotateDeg: 0,
          embossEnabled: true,
          embossDepthMm: 0.4,
          maxSubdivideIterations: 3,
        },
        
        // Main Process - Pads
        main_pads_pad: {
          enabled: true
        },
        main_pads_position: {
          enabled: true
        },
        main_pads_adjust: {
          enabled: true
        },
        
        // Main Process - Finalization
        main_finalization_review: {
          enabled: true
        },
        main_finalization_export: {
          enabled: true
        },
        main_finalization_save: {
          enabled: true
        }
      },
      setToolSetting: (tool, setting, value) => {
        set(state => ({
          toolSettings: {
            ...state.toolSettings,
            [tool]: {
              ...state.toolSettings[tool],
              [setting]: value
            }
          }
        }));
      },

      // ===== 3D MODEL STATE =====
      modelLoaded: false,
      setModelLoaded: (loaded) => set({ modelLoaded: loaded }),

      // Model path selection for debug/testing
      selectedModelPath: null,
      setSelectedModelPath: (path) => set({ selectedModelPath: path }),

      // ===== UPLOADED MODEL STATE =====
      uploadedModelData: null, // Stores uploaded model file data and Blob URLs
      setUploadedModelData: (data) => set({ uploadedModelData: data }),

      // Model scaling state
      modelScaleInfo: null, // Stores scaling information from ModelScaler
      setModelScaleInfo: (info) => set({ modelScaleInfo: info }),

      // Load-time mesh stats: { simplify: { before, after, ... }, topology: {...} }
      meshStats: null,
      setMeshStats: (stats) => set({ meshStats: stats }),

      modelScaleUnit: 'mm', // Default unit override: 'mm' (options: 'auto', 'mm', 'cm', 'm', 'inches')
      setModelScaleUnit: (unit) => set({ modelScaleUnit: unit }),

      /** Manual multiplier applied on top of auto scale. */
      modelManualScale: 1.0,
      setModelManualScale: (scale) => set({ modelManualScale: scale }),

      /**
       * Socket align mesh TRS (session memory). Mesh keeps TRS in-scene too; this re-applies after reload/replace.
       * @type {{ position: number[], quaternion: number[], scale: number[], qBase?: number[] } | null}
       */
      alignmentSocketPose: null,

      captureAlignmentSocketPose: () => {
        const mesh = get().sceneRefs?.shadedMesh;
        if (!mesh) return;
        const pose = {
          position: mesh.position.toArray(),
          quaternion: mesh.quaternion.toArray(),
          scale: mesh.scale.toArray(),
          qBase: getAlignmentSocketQBaseArray(),
        };
        logLockingSocketScale('captureAlignmentSocketPose', {
          scale: pose.scale,
          flexDeg: get().alignmentSocketRedRingXDeg,
          abdDeg: get().alignmentSocketAbductionZDeg,
        });
        trackMeshScale(mesh, 'captureAlignmentSocketPose');
        set({ alignmentSocketPose: pose });
      },

      applyAlignmentSocketPose: () => {
        const pose = get().alignmentSocketPose;
        const mesh = get().sceneRefs?.shadedMesh;
        if (!pose || !mesh) return false;
        // Post-attachment CSG bakes alignment into vertices; object TRS must stay identity.
        if (mesh.userData?.lockingWorldBaked) return false;
        const scaleBefore = mesh.scale.clone();
        logLockingSocketScale('applyAlignmentSocketPose:before', {
          meshScale: scaleBefore.toArray(),
          poseScale: pose.scale,
          flexDeg: get().alignmentSocketRedRingXDeg,
          abdDeg: get().alignmentSocketAbductionZDeg,
        });
        mesh.position.fromArray(pose.position);
        // Scale is set at model load only; socket align adjusts position + rotation.
        if (pose.qBase?.length === 4) {
          setAlignmentSocketQBaseFromArray(pose.qBase);
        } else {
          mesh.quaternion.fromArray(pose.quaternion);
          rederiveAlignmentSocketQBaseFromMesh();
        }
        applyAlignmentSocketAnglesToMesh();
        mesh.updateMatrixWorld(true);
        logScaleIfChanged('applyAlignmentSocketPose:after', mesh, {
          x: scaleBefore.x,
          y: scaleBefore.y,
          z: scaleBefore.z,
        }, { poseScale: pose.scale });
        get().bumpSocketTransformRevision();
        return true;
      },

      /** Alignment step: TransformControls mode when `main_alignment_socket_pose` is active. */
      alignmentSocketTransformMode: 'translate',
      setAlignmentSocketTransformMode: (alignmentSocketTransformMode) =>
        set({ alignmentSocketTransformMode }),

      /**
       * Alignment: flexion (degrees) about mesh-local +X (step-2 condyle frame), axis follows socket orientation.
       * Factored from gizmo pose via internal qBase in `socketAlignRedRing.js`.
       */
      alignmentSocketRedRingXDeg: 0,
      setAlignmentSocketRedRingXDeg: (alignmentSocketRedRingXDeg) => {
        const mesh = get().sceneRefs?.shadedMesh;
        logLockingSocketScale('setAlignmentSocketRedRingXDeg', {
          deg: alignmentSocketRedRingXDeg,
          meshScale: mesh ? mesh.scale.toArray() : null,
        });
        set({ alignmentSocketRedRingXDeg });
      },

      /**
       * Alignment: abduction (degrees) about mesh-local +Z (mid-patellar forward), axis follows socket orientation.
       */
      alignmentSocketAbductionZDeg: 0,
      setAlignmentSocketAbductionZDeg: (alignmentSocketAbductionZDeg) => {
        const mesh = get().sceneRefs?.shadedMesh;
        logLockingSocketScale('setAlignmentSocketAbductionZDeg', {
          deg: alignmentSocketAbductionZDeg,
          meshScale: mesh ? mesh.scale.toArray() : null,
        });
        set({ alignmentSocketAbductionZDeg });
      },

      /** Gizmo base rotation (quaternion xyzw); flex/abduction apply on top. */
      alignmentSocketQBase: null,
      setAlignmentSocketQBase: (alignmentSocketQBase) => set({ alignmentSocketQBase }),

      /** Foot reference pitch (deg) about toe hinge, world +Z — see `useAlignmentLadder`. */
      alignmentFootToePitchDeg: 0,
      setAlignmentFootToePitchDeg: (alignmentFootToePitchDeg) =>
        set({ alignmentFootToePitchDeg }),

      cameraPosition: null,
      setCameraPosition: (cameraPosition) => set({cameraPosition: cameraPosition}),

      setCamera: (newCamera) => {
        const { sceneRefs } = get(); // Use get() to access current state

        // On the first switch, save the original camera so we can revert later
        if (!sceneRefs.originalCamera) {
            sceneRefs.originalCamera = sceneRefs.camera;
        }

        // Update OrbitControls to use the new camera
        if (sceneRefs.controls) {
            sceneRefs.controls.object = newCamera;
            sceneRefs.controls.update(); // Force an update
        }
        
        // Update the state to trigger re-renders in components
        set({
            sceneRefs: { ...sceneRefs, camera: newCamera }
        });
      },
        
      // Action to restore the original camera
      restoreOriginalCamera: () => {
          const { sceneRefs } = get();
          if (sceneRefs.originalCamera) {
              setCamera(sceneRefs.originalCamera);
              // Clear originalCamera so it can be set again next time
              set(state => ({
                  sceneRefs: { ...state.sceneRefs, originalCamera: null }
              }));
          }
      },
      // Model transformations
      modelTransform: {
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 }
      },
      setModelTransform: (transform) => set({ modelTransform: transform }),

      // ===== MEASUREMENT TOOL =====
      measuringMode: false,
      setMeasuringMode: (mode) => set({ measuringMode: mode }),

      points: [],
      setPoints: (points) => set({ points }),

      distance: null,
      setDistance: (distance) => set({ distance }),

      pointMeshes: [],
      setPointMeshes: (meshes) => set({ pointMeshes: meshes }),

      measureLine: null,
      setMeasureLine: (line) => set({ measureLine: line }),

      circumference: null,
      setCircumference: (value) => set({ circumference: value }),
      circumferenceError: null,
      setCircumferenceError: (msg) => set({ circumferenceError: msg }),
      circumferenceContourLine: null,
      setCircumferenceContourLine: (line) => set({ circumferenceContourLine: line }),

      // ===== WIREFRAME TOGGLE =====
      wireframeVisible: false,
      setWireframeVisible: (visible) => set({ wireframeVisible: visible }),
      toggleWireframe: () => {
        const sm = get().sceneRefs?.shadedMesh;
        if(sm)
        {
          const toggle = get().wireframeVisible;
          sm.material.wireframe = !toggle;
          set({wireframeVisible: !toggle});
        }
      },

      // ===== UNDO/REDO SYSTEM =====
      history: [],
      historyIndex: -1,

      execCommand: (command) => {
        // A display-only tint may be sitting on the LIVE socket's vertex colours
        // right now — measure reduction's band preview, or the read-only overlay
        // behind the circumference details popover, which outlives its tool and so
        // can still be up when an unrelated tool applies. Every tool bakes its new
        // geometry from this socket, so the tint has to come off here or the bands
        // are baked in permanently. `captureUndoSnapshot` only cleans its own
        // clone, so it cannot cover this. No-op when nothing is tinted.
        stripPreviewTint(get().sceneRefs?.shadedMesh?.geometry);

        // Snapshot the pre-apply socket for tools that declare a `toolId`. The
        // command's own closure is the fast undo path, but it dies on reload /
        // session restore; the snapshot is what makes Undo survive that.
        const toolId = command?.toolId ?? null;
        let prePayload = null;
        if (toolId) {
          captureUndoSnapshot(toolId);
          // The pool's copy *is* the pre-apply socket — hand the same payload to
          // the uploader rather than cloning the geometry a second time.
          prePayload = peekUndoSnapshot(toolId);
        }

        // Measures the geometry work alone. Deliberately not wall-clock apply
        // time: preview teardown and React updates are not reproduced by a
        // replay, so including them would inflate every estimate.
        const startedAt = performance.now();
        command.execute();
        const durationMs = performance.now() - startedAt;

        if (toolId) {
          notifyToolApplied({
            toolId,
            durationMs,
            vertexCount:
              get().sceneRefs?.shadedMesh?.geometry?.attributes?.position?.count ?? null,
            prePayload,
            // Tools opt into recipe undo by attaching serialisable params to their
            // command; until then only order and timing are recorded.
            params: command.params ?? command.replayParams ?? {},
          });
        }

        set(state => {
          const nextIndex = state.historyIndex + 1;
          const newHistory = state.history.slice(0, nextIndex);
          newHistory.push(command);
          return {
            history: newHistory,
            historyIndex: nextIndex
          };
        });
      },

      addToHistory: (action) => {
        set(state => {
          const newHistory = state.history.slice(0, state.historyIndex + 1);
          newHistory.push(action);
          return {
            history: newHistory,
            historyIndex: newHistory.length - 1
          };
        });
      },

      undo: () => {
        set(state => {
            if (state.historyIndex < 0) return state; // Nothing to undo

            const commandToUndo = state.history[state.historyIndex];
            if (commandToUndo.undo) {
                commandToUndo.undo(); // <-- This will call our trimCommand.undo()
            }

            return { historyIndex: state.historyIndex - 1 };
        });
      },

      redo: () => {
        const { history, historyIndex } = get();
        if (historyIndex < history.length - 1) {
          const nextIndex = historyIndex + 1;
          history[nextIndex].execute();
          set({ historyIndex: nextIndex });
        }
      },

      // Reset history up to a specific command
      resetHistoryUpTo: (targetCommand) => {
        set(state => {
          const { history, historyIndex } = state;
          
          // Find the index of the target command in history
          const targetIndex = history.findIndex(cmd => cmd === targetCommand);
          
          if (targetIndex === -1) {
            console.warn('Target command not found in history');
            return state;
          }
          
          // Keep history up to and including the target command
          const newHistory = history.slice(0, targetIndex + 1);
          const newHistoryIndex = targetIndex;
          
          return {
            history: newHistory,
            historyIndex: newHistoryIndex
          };
        });
      },

      clearHistory: () => {
        set({ history: [], historyIndex: -1 });
      },

      // ===== UI STATE =====
      // Right panel visibility
      rightPanelVisible: true,
      setRightPanelVisible: (visible) => set({ rightPanelVisible: visible }),

      // Secondary camera view toggles (which of the 6 views are shown in the grid)
      // Constraint: max 2 active views. If enabling a 3rd, remove the oldest active one.
      viewToggles: { front: true, back: false, top: false, bottom: false, left: true, right: false },
      viewSelectionOrder: ['front', 'left'],
      setViewToggle: (viewId, visible) => set((state) => {
        const nextToggles = { ...state.viewToggles };
        let nextOrder = [...(state.viewSelectionOrder || [])].filter((id) => nextToggles[id]);

        if (visible) {
          if (!nextToggles[viewId]) {
            if (nextOrder.length >= 2) {
              const oldestViewId = nextOrder.shift();
              if (oldestViewId) nextToggles[oldestViewId] = false;
            }
            nextToggles[viewId] = true;
            nextOrder.push(viewId);
          }
        } else {
          nextToggles[viewId] = false;
          nextOrder = nextOrder.filter((id) => id !== viewId);
        }

        return {
          viewToggles: nextToggles,
          viewSelectionOrder: nextOrder
        };
      }),
      setViewToggles: (toggles) => set(() => {
        const allViewIds = ['front', 'back', 'top', 'bottom', 'left', 'right'];
        const normalized = allViewIds.reduce((acc, id) => {
          acc[id] = !!toggles?.[id];
          return acc;
        }, {});

        const active = allViewIds.filter((id) => normalized[id]);
        while (active.length > 2) {
          const removedId = active.shift();
          normalized[removedId] = false;
        }

        return {
          viewToggles: normalized,
          viewSelectionOrder: active
        };
      }),

      // Left toolbar visibility
      leftToolbarVisible: true,
      setLeftToolbarVisible: (visible) => set({ leftToolbarVisible: visible }),

      // Loading states
      isLoading: false,
      loadingMessage: null,
      loadingProgress: 0, // 0-100, drives the model-load progress bar
      /** Unscoped `setIsLoading(true)` is still pending a matching `false`. */
      legacyLoading: false,
      /** @type {{ token: string, owner: string, message: string|null, at: number }[]} */
      loadingOwners: [],

      setIsLoading: (loading, message = null) =>
        set((s) => {
          if (loading) {
            // Only the transitions matter; a repeat of the same on-state is noise.
            if (!s.isLoading || s.loadingMessage !== message) {
              traceLoading('setIsLoading', {
                was: s.isLoading,
                now: true,
                message,
                owners: s.loadingOwners,
              })
            }
            return {
              isLoading: true,
              legacyLoading: true,
              loadingMessage: message,
              loadingProgress: 0,
            }
          }

          // A scoped owner outranks the legacy flag. Without this, code the
          // owner itself called into (the replay engine, a ThreeScene effect
          // cleanup firing on the same navigation) tears the overlay down
          // mid-operation.
          const held = s.loadingOwners.length > 0
          if (s.isLoading) {
            traceLoading(held ? 'setIsLoading(false) SUPPRESSED' : 'setIsLoading', {
              was: s.isLoading,
              now: held,
              message: held ? s.loadingMessage : null,
              owners: s.loadingOwners,
            })
          }
          return {
            isLoading: held,
            legacyLoading: false,
            loadingMessage: held ? s.loadingMessage : null,
            loadingProgress: 0,
          }
        }),

      /**
       * Claim the overlay for a named operation. Pair with {@link endLoading} in
       * a `finally` — an owner that is never released holds the overlay open.
       *
       * @param {string} owner  short label, shows up in the trace
       * @param {string|null} [message]
       * @returns {string} token to pass to `endLoading`
       */
      beginLoading: (owner, message = null) => {
        const token = `${owner}#${++loadingTokenSeq}`
        set((s) => {
          const owners = [...s.loadingOwners, { token, owner, message, at: loadingNow() }]
          traceLoading(`beginLoading(${owner})`, {
            was: s.isLoading,
            now: true,
            message,
            owners,
          })
          return {
            loadingOwners: owners,
            isLoading: true,
            loadingMessage: message ?? s.loadingMessage,
            loadingProgress: 0,
          }
        })
        return token
      },

      /** Release a {@link beginLoading} claim. Unknown tokens are ignored. */
      endLoading: (token) =>
        set((s) => {
          const owners = s.loadingOwners.filter((o) => o.token !== token)
          if (owners.length === s.loadingOwners.length) return s

          const held = owners.length > 0 || s.legacyLoading
          const message = held
            ? owners[owners.length - 1]?.message ?? s.loadingMessage
            : null
          traceLoading(`endLoading(${token})`, {
            was: s.isLoading,
            now: held,
            message,
            owners,
          })
          return {
            loadingOwners: owners,
            isLoading: held,
            loadingMessage: message,
            loadingProgress: held ? s.loadingProgress : 0,
          }
        }),

      /** Diagnostics for a stuck overlay: who is holding it, and for how long. */
      describeLoadingOwners: () => {
        const { loadingOwners, legacyLoading, loadingMessage } = get()
        const now = loadingNow()
        return {
          message: loadingMessage,
          legacyLoading,
          owners: loadingOwners.map((o) => ({
            owner: o.owner,
            heldForMs: Math.round(now - o.at),
          })),
        }
      },
      /**
       * Keep the loading overlay up (or turn it on) WITHOUT resetting the
       * progress bar to 0. Used to hand a load off between owners (e.g. the
       * checkpoint download → the phased model loader) so the bar advances
       * monotonically instead of snapping back to 0.
       */
      setLoadingActive: (message = null) =>
        set((s) => ({
          isLoading: true,
          loadingMessage: message ?? s.loadingMessage,
        })),
      setLoadingMessage: (message) => set({ loadingMessage: message }),
      setLoadingProgress: (progress) =>
        set({ loadingProgress: Math.max(0, Math.min(100, progress)) }),

      // Worker-specific loading states
      isWorkerBusy: false,
      workerProgress: null, // { operation: string, progress: number (0-100) }
      setWorkerLoading: (busy, operation = null, progress = null) => {
        console.log('[EditorStore] setWorkerLoading called with:', { busy, operation, progress });
        set({
          isWorkerBusy: busy,
          workerProgress: busy ? { operation, progress } : null
        });
        console.log('[EditorStore] State updated. New state:', { isWorkerBusy: busy, workerProgress: busy ? { operation, progress } : null });
      },

      /** Flares: debounced slider rebuild (main-thread reset + worker apply) */
      flareParamPreviewBusy: false,
      setFlareParamPreviewBusy: (busy) => set({ flareParamPreviewBusy: !!busy }),

      // Error state
      error: null,
      setError: (error) => set({ error }),

      // ===== UTILITY FUNCTIONS =====
      resetScene: () => {
        set({
          activeTool: 'none',
          measuringMode: false,
          paintingMode: false,
          points: [],
          distance: null,
          wireframeVisible: false,
          modelTransform: {
            position: { x: 0, y: 0, z: 0 },
            rotation: { x: 0, y: 0, z: 0 },
            scale: { x: 1, y: 1, z: 1 }
          }
        });
      },

      // Clear all tool states
      clearToolStates: () => {
        useMLReductionStore.getState().resetMLReduction();

        // Orbit-enabled is the one piece of tool state that does NOT live in
        // this store — it is a flag on the OrbitControls object, so clearing the
        // store leaves it exactly as the last tool left it. Every tool that
        // parks orbit (a brush stroke, a gizmo drag, painting mode, a ring
        // pick) hands it back when its own interaction ends, but a navigation
        // unmounts the tool instead and that never happens.
        //
        // Dropping every hold is correct here specifically because this runs on
        // navigation: all tools on the step are going away, so none of them can
        // still be mid-interaction. Only `enabled` is affected — see
        // lib/orbitControlsGuard.
        releaseAllOrbitParks(get().sceneRefs?.controls);

        set({
          activeTool: 'none',
          measuringMode: false,
          paintingMode: false,
          points: [],
          distance: null,
          alignmentSocketTransformMode: 'translate',
          alignmentSocketRedRingXDeg: 0,
          alignmentSocketAbductionZDeg: 0,
          alignmentSocketQBase: null,
          alignmentFootToePitchDeg: 0,
          alignmentSocketPose: null,
        });
      }
    }))
    ),
    {
      name: 'editor-store',
      partialize: (state) => ({
        selectedModelPath: state.selectedModelPath,
        modelScaleUnit: state.modelScaleUnit,
      }),
    }
  )
);

export default useEditorStore;
